/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useCallback, useContext, useState, type ReactNode } from "react"
import { API_ROUTES } from "../configuration/RoutesApi"
import { GetRequest } from "../services/ApiClientService"
import { companyMemory } from "../Objects/Memory/Memory"
import type { CompanyModuleStatusEntry } from "../Objects/Company/CompanyModulesStatus"
import type { SystemModuleType } from "../Enum/SystemModuleType"

interface CompanyModulesStatusContextData {
  statuses: CompanyModuleStatusEntry[]
  isLoading: boolean
  refresh: () => Promise<CompanyModuleStatusEntry[] | undefined>
  isModuleEnabled: (moduleCode?: SystemModuleType) => boolean
}

const CompanyModulesStatusContext = createContext<CompanyModulesStatusContextData | undefined>(undefined)

interface CompanyModulesStatusProviderProps {
  children: ReactNode
}

export const CompanyModulesStatusProvider: React.FC<CompanyModulesStatusProviderProps> = ({ children }) => {
  const [statuses, setStatuses] = useState<CompanyModuleStatusEntry[]>([])

  const [isLoading, setIsLoading] = useState(false)

  const refresh = useCallback(async () => {
    setIsLoading(true)

    try {
      const response = await GetRequest<CompanyModuleStatusEntry[]>(API_ROUTES.COMPANY.GET_MODULES(companyMemory.id))

      if (!response.success) return undefined

      setStatuses(response.data ?? [])

      return response.data
    } finally {
      setIsLoading(false)
    }
  }, [])

  const isModuleEnabled = useCallback(
    (moduleCode?: SystemModuleType) => {
      if (!moduleCode) return true

      const entry = statuses.find((status) => status.code === moduleCode)

      return entry?.isEnabled ?? true
    },
    [statuses],
  )

  return (
    <CompanyModulesStatusContext.Provider value={{ statuses, isLoading, refresh, isModuleEnabled }}>
      {children}
    </CompanyModulesStatusContext.Provider>
  )
}

export const useCompanyModulesStatus = (): CompanyModulesStatusContextData => {
  const context = useContext(CompanyModulesStatusContext)

  if (!context) {
    throw new Error("useCompanyModulesStatus deve ser usado dentro de um CompanyModulesStatusProvider")
  }

  return context
}
